import React, {useState} from 'react';
import {Space, Spin} from 'antd';

import {Button, Modal, Typography} from '@components/common';
import {trackError} from '@utils/createSentry';
import SharingService from '@utils/apis/SharingService';

enum ModalState {
  Confirm,
  Loading,
  Success,
  Error,
}

export type RevokeAllModalProps = {
  isVisible: boolean
  closeModal: any
  object: any
  organization: any
  onRevokeSuccess: any
}

const RevokeAllModal = ({
  isVisible,
  closeModal,
  object,
  organization,
  onRevokeSuccess
}: RevokeAllModalProps): JSX.Element => {
  const [modalState, setModalState] = useState(ModalState.Confirm);
  const [errorMessage, setErrorMessage] = useState('');

  const revokeAll = async () => {
    try {
      setModalState(ModalState.Loading);
      await SharingService.revokeKeywordsFromActor(object, organization);
      setModalState(ModalState.Success);
      onRevokeSuccess();
    } catch (e) {
      setModalState(ModalState.Error);
      setErrorMessage(e.message);
      trackError(e);
    }
  };

  const ModalMessage = () => {
    switch (modalState) {
    case ModalState.Confirm:
      return (
        <Typography variant="primary" as="p">
            You are about to revoke access to all rose objects that you have
            shared with <b>{organization.code}</b>
        </Typography>
      );
    case ModalState.Loading:
      return (
        <Space direction="vertical" align="center">
          <Spin size="large" />
        </Space>
      );
    case ModalState.Success:
      return <Typography variant="primary" as="p">Successfully revoked all objects</Typography>;
    case ModalState.Error:
      return <Typography variant="primary" as="p">{errorMessage}</Typography>;
    default:
      return null;
    }
  };

  const modalFooter = () => {
    switch (modalState) {
    case ModalState.Confirm:
      return [
        <Button id="cancel" key="cancel" onClick={closeModal}>
            Cancel
        </Button>,
        <Button id="revoke" key="revoke" onClick={revokeAll}>
            Revoke
        </Button>
      ];
    case ModalState.Loading:
      return null;
    case ModalState.Success || ModalState.Error:
      return [
        <Button key="ok" onClick={closeModal}>
            OK
        </Button>
      ];
    default:
      return null;
    }
  };

  const getModalTitle = () => {
    switch (modalState) {
    case ModalState.Confirm:
      return 'Revoke All';
    case ModalState.Loading:
      return 'Loading...';
    case ModalState.Success:
      return 'Success';
    case ModalState.Error:
      return 'Error occured';
    default:
      return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      visible={isVisible}
      footer={modalFooter()}
      onCancel={closeModal}
    >
      <ModalMessage />
    </Modal>
  );
};

export default RevokeAllModal;
